// SMS CENTRUM (Mirko 22.7. v noci): písanie SMS z hlavného panelu + história vlákien ako na iPhone.
// Vlákna = zoskupené podľa čísla (mená z dopytov/zákazníkov/zamestnancov/partnerov), bubliny odoslaných správ,
// pod bublinou KTO poslal (viditeľné pre všetkých — celá komunikácia na očiach). Odpovede zákazníkov brána
// zatiaľ neprijíma (odosielateľ je textové „KAMIR") — vlákno je jednosmerné, prijímanie = budúca fáza s číslom.
// ÚSTAVA: žiadne emoji.
import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useDirtyFlag } from '../hooks/useDirtyState.js'   // straz_v3 (28. 9. 2026): rozpísaná SMS drží stráž odchodu (ústava 53.1.b)
import { COLORS, TYPO, RADIUS } from '../theme.js'
import { smsPresety, smsKontakty } from '../components/SmsButton.jsx'
import { normPhone } from '../utils/phone.js'
import { VyberCisla, UlozitKontakt } from '../components/KontaktyPartnera.jsx'   // p107 — chrbtica: číslo z celej databázy, uložiť kontakt

const tok = () => localStorage.getItem('kamir_token') || ''
const api = (u, o = {}) => fetch(u, { ...o, headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + tok(), ...(o.headers || {}) } })
const fmtCas = (t) => { try { const d = new Date(String(t).replace(' ', 'T') + 'Z'); return isNaN(d) ? '' : d.toLocaleTimeString('sk-SK', { hour: '2-digit', minute: '2-digit' }) } catch (_) { return '' } }
const fmtDen = (t) => { try { const d = new Date(String(t).replace(' ', 'T') + 'Z'); return isNaN(d) ? '' : d.toLocaleDateString('sk-SK', { day: '2-digit', month: '2-digit', year: 'numeric' }) } catch (_) { return '' } }
const TYP_L = { dopyt: 'dopyt', zakaznik: 'zákazník', zamestnanec: 'zamestnanec', partner: 'partner', kontakt: 'kontaktná osoba' }   // p93: register osôb (131.10)

export default function SmsCentrum() {
  const [vlakna, setVlakna] = useState([])
  const [sel, setSel] = useState(null)          // key vybraného vlákna
  const [msgs, setMsgs] = useState([])
  const [q, setQ] = useState('')
  const [novaTo, setNovaTo] = useState('')      // nové vlákno: číslo
  const [text, setText] = useState('')
  useDirtyFlag(!!String(text || '').trim(), { key: 'sms-text', discard: () => setText('') })   // straz_v3: odoslanie tlačidlom; dialóg ponúkne Zahodiť / Zrušiť
  const [busy, setBusy] = useState(false)
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [presety, setPresety] = useState([])
  const [kontakty, setKontakty] = useState([])
  const [ulozit, setUlozit] = useState(null)   // p107: { telefon } — vlákno s číslom, ktoré Matrix nepozná → uložiť kontakt
  useEffect(() => { smsPresety().then(setPresety); smsKontakty().then(setKontakty) }, [])
  const thrRef = useRef(null)

  const load = useCallback(async () => {
    try { const r = await api('/api/sms/vlakna'); setVlakna(await r.json()) } catch (_) {} finally { setLoading(false) }
  }, [])
  useEffect(() => { load(); api('/api/sms/status').then(r => r.json()).then(setStatus).catch(() => {}) }, [load])
  const cur = vlakna.find(v => v.key === sel) || null
  const loadVlakno = useCallback(async (phone) => {
    try { const r = await api('/api/sms/vlakno?phone=' + encodeURIComponent(phone)); setMsgs(await r.json()) } catch (_) { setMsgs([]) }
  }, [])
  useEffect(() => { if (cur) loadVlakno(cur.phone); else setMsgs([]) }, [sel])   // eslint-disable-line
  useEffect(() => { if (thrRef.current) thrRef.current.scrollTop = thrRef.current.scrollHeight }, [msgs])

  const configured = !!(status && status.configured)
  // počítadlo — rovnaká transformácia ako brána (GSM-7)
  const plainLen = text.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[„“”‘’‚]/g, "'").replace(/[–—]/g, '-').replace(/…/g, '...').replace(/·/g, '-').length
  const segments = plainLen <= 160 ? 1 : Math.ceil(plainLen / 153)

  const send = async () => {
    const to = cur ? cur.phone : novaTo.trim()
    if (busy || !to || !text.trim()) return
    setBusy(true)
    try {
      const r = await api('/api/sms/send', { method: 'POST', body: JSON.stringify({ to, text: text.trim() }) })
      const d = await r.json().catch(() => ({}))
      if (!r.ok || !d.ok) throw new Error(d.error || 'odoslanie zlyhalo')
      setText('')
      await load()
      const n9 = to.replace(/\D/g, '').slice(-9)
      setSel(n9); loadVlakno(to); setNovaTo('')
      // p107 (Mirko 22. 9. 2026: „ak napíše niekto SMS niekomu — výzva na uloženie kontaktu")
      const zoz = await smsKontakty(true); setKontakty(zoz)
      if (n9.length === 9 && !zoz.some(k => k.n === n9 && String(k.meno || '').trim())) setUlozit({ telefon: normPhone(to) })
    } catch (e) { alert('SMS: ' + e.message) } finally { setBusy(false) }
  }

  const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
  const list = vlakna.filter(v => !q.trim() || norm(v.meno + ' ' + v.phone + ' ' + (v.last_text || '') + ' ' + (v.last_kto || '')).includes(norm(q)))

  // deň separátory ako na iPhone
  const rendered = []
  let lastDay = ''
  for (const m of msgs) {
    const den = fmtDen(m.created_at)
    if (den !== lastDay) { rendered.push({ sep: den, id: 'sep' + m.id }); lastDay = den }
    rendered.push(m)
  }

  return (
    <div style={{ position: 'absolute', inset: 0, padding: '12px 20px', overflow: 'hidden', display: 'flex', flexDirection: 'column', boxSizing: 'border-box' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>SMS <span style={{ fontSize: 13, fontWeight: 400, color: '#64748b' }}>· celá odoslaná komunikácia firmy · odosielateľ „KAMIR"</span></h2>
        {!configured && <span style={{ fontSize: 12, fontWeight: 700, color: '#b45309', background: '#fff7ed', borderRadius: 8, padding: '4px 10px' }}>SMS brána nie je nakonfigurovaná</span>}
        <button onClick={() => { setSel(null); setNovaTo(''); setText('') }} style={{ marginLeft: 'auto', padding: '9px 16px', border: 'none', background: '#D85A30', color: '#fff', borderRadius: 9, cursor: 'pointer', fontWeight: 700, fontFamily: 'inherit' }}>+ Nová SMS</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 340px) 1fr', gap: 12, flex: 1, minHeight: 0 }}>
        {/* ZOZNAM KONVERZÁCIÍ */}
        <div style={{ background: '#f8fafc', borderRadius: 12, display: 'flex', flexDirection: 'column', minHeight: 0, border: '1px solid #eef2f7' }}>
          <div style={{ padding: 8 }}>
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Hľadať meno, číslo, text…"
              style={{ width: '100%', padding: '8px 11px', border: '1px solid #d6dde6', borderRadius: 9, fontFamily: 'inherit', fontSize: 13, outline: 'none', boxSizing: 'border-box', background: '#fff' }} />
          </div>
          <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {loading ? <div style={{ color: '#94a3b8', padding: 14 }}>Načítavam…</div> :
              !list.length ? <div style={{ color: '#94a3b8', padding: 14, fontSize: 13 }}>Zatiaľ žiadne SMS.</div> :
              list.map(v => (
                <div key={v.key} onClick={() => setSel(v.key)}
                  style={{ padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', background: sel === v.key ? '#fff' : 'transparent', borderLeft: '3px solid ' + (sel === v.key ? '#D85A30' : 'transparent') }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <b style={{ fontSize: 13.5, color: COLORS.text, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v.meno || v.phone}</b>
                    <span style={{ fontSize: 11, color: '#94a3b8', flexShrink: 0 }}>{fmtDen(v.last_at)} {fmtCas(v.last_at)}</span>
                  </div>
                  <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 1 }}>
                    {v.meno ? v.phone + ' · ' : ''}{v.typ ? (TYP_L[v.typ] || v.typ) + ' · ' : ''}{v.n} SMS
                  </div>
                  <div style={{ fontSize: 12, color: v.last_status === 'sent' ? '#475569' : '#dc2626', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {v.last_kto ? v.last_kto + ': ' : ''}{v.last_text}
                  </div>
                </div>
              ))}
          </div>
        </div>

        {/* VLÁKNO */}
        <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #eef2f7', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 10 }}>
            {cur ? (
              <>
                <b style={{ fontSize: 14.5 }}>{cur.meno || cur.phone}</b>
                <span style={{ fontSize: 12, color: '#94a3b8' }}>{cur.meno ? cur.phone : ''}{cur.typ ? ' · ' + (TYP_L[cur.typ] || cur.typ) : ''}</span>
                {!cur.meno && !ulozit && (
                  <button onClick={() => setUlozit({ telefon: normPhone(cur.phone) })}
                    style={{ marginLeft: 'auto', padding: '5px 11px', border: '1px solid #86efac', background: '#f0fdf4', color: '#15803d', borderRadius: 8, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 700, fontSize: 12 }}>
                    Uložiť kontakt
                  </button>
                )}
              </>
            ) : (
              <>
                <b style={{ fontSize: 13, flexShrink: 0 }}>Nová SMS — komu:</b>
                <div style={{ width: 300 }}>
                  <VyberCisla value={novaTo} onChange={v => setNovaTo(/^[\d\s+()/-]+$/.test(v) ? normPhone(v) : v)} placeholder="Meno, firma alebo číslo…" />
                </div>
                <span style={{ fontSize: 11.5, color: '#94a3b8' }}>ak číslo patrí dopytu, SMS sa zapíše aj do jeho vlákna</span>
              </>
            )}
          </div>
          {ulozit && (!cur || String(cur.phone || '').replace(/\D/g, '').slice(-9) === ulozit.telefon.replace(/\D/g, '').slice(-9)) && (
            <div style={{ padding: '10px 14px', borderBottom: '1px solid #f1f5f9', background: '#f8fafc' }}>
              <div style={{ fontSize: 12.5, color: '#475569', marginBottom: 6 }}>Číslo <b>{ulozit.telefon}</b> Matrix nepozná. Ulož ho ku kontaktom — nabudúce ho nájdeš podľa mena v SMS, e-maile aj na každom doklade.</div>
              <UlozitKontakt telefon={ulozit.telefon} zrusText="Teraz nie" nadpis="ULOŽIŤ KONTAKT — ku ktorému partnerovi patrí?"
                onUlozene={() => { setUlozit(null); smsKontakty(true).then(setKontakty); load() }} onZrus={() => setUlozit(null)} />
            </div>
          )}
          <div ref={thrRef} style={{ flex: 1, overflowY: 'auto', minHeight: 0, padding: '12px 16px', background: '#fbfbfd' }}>
            {cur && !rendered.length && <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', marginTop: 20 }}>Žiadne správy.</div>}
            {!cur && <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', marginTop: 20 }}>Vyber konverzáciu vľavo alebo napíš novú SMS.</div>}
            {rendered.map(m => m.sep ? (
              <div key={m.id} style={{ textAlign: 'center', margin: '12px 0 8px', fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>{m.sep}</div>
            ) : (
              <div key={m.id} style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                <div style={{ maxWidth: '72%' }}>
                  <div style={{ background: m.status === 'sent' ? '#2fb457' : '#dc2626', color: '#fff', borderRadius: '16px 16px 4px 16px', padding: '8px 13px', fontSize: 13.5, whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
                    {m.text}
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 10.5, color: '#94a3b8', marginTop: 2, paddingRight: 4 }}>
                    {m.kto ? m.kto + ' · ' : ''}{fmtCas(m.created_at)}{m.status !== 'sent' ? ' · NEODOSLANÁ' : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ borderTop: '1px solid #f1f5f9', padding: 10, display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              {presety.length > 0 && (
                <select value="" onChange={e => { const p = presety.find(x => String(x.id) === e.target.value); if (p) setText(p.text) }}
                  style={{ marginBottom: 4, padding: '6px 9px', border: '1px solid #d6dde6', borderRadius: 8, fontFamily: 'inherit', fontSize: 12 }}>
                  <option value="">Predloha…</option>
                  {presety.map(p => <option key={p.id} value={p.id}>{p.nazov}</option>)}
                </select>
              )}
              <textarea value={text} onChange={e => setText(e.target.value)} rows={2} placeholder={configured ? 'Napíš SMS… (Enter = nový riadok)' : 'SMS brána nie je zapojená'}
                disabled={!configured}
                style={{ width: '100%', padding: '9px 12px', border: '1px solid #d6dde6', borderRadius: 10, fontFamily: 'inherit', fontSize: 13.5, boxSizing: 'border-box', outline: 'none', resize: 'none' }} />
              <div style={{ fontSize: 10.5, color: plainLen > 160 ? '#b45309' : '#94a3b8', marginTop: 2 }}>{plainLen} znakov · {segments} SMS · diakritika sa odstráni</div>
            </div>
            <button onClick={send} disabled={busy || !configured || !text.trim() || (!cur && !novaTo.trim())}
              style={{ padding: '10px 20px', border: 'none', background: busy || !configured ? '#cbd5e1' : '#2fb457', color: '#fff', borderRadius: 10, cursor: busy ? 'wait' : 'pointer', fontWeight: 800, fontFamily: 'inherit', fontSize: 13.5, marginBottom: 18 }}>
              {busy ? 'Odosielam…' : 'Odoslať'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
